import {
  useExportOrganization,
  useOrganizationBulkDelete,
  useOrganizations,
} from "@/api/organization";
import { TableDefault } from "@/components/table/table-default";
import { TableForm } from "@/components/table/table-form";
import {
  DataConfig,
  useDefaultDataConfig,
} from "@/components/table/useDefaultDataConfig";
import { generateCustomSpec } from "@/utils/helper";
import {
  swalDelete,
  swalError,
  swalErrorDefault,
  swalSuccess,
} from "@/utils/lib/swal";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { ROLE } from "@/utils/constant";
import { useCurrentUser } from "../auth/useCurrentUser";
import { DownloadButton } from "@/components/ui/button";
import { nullElement } from "@/components/null-element";

const customAdminSpec = generateCustomSpec({
  path: "",
  fields: [
    {
      label: "Company Code",
      name: "companyCode",
    },
    {
      label: "Company Name",
      name: "companyName",
    },
    {
      label: "Department",
      name: "department",
    },
    {
      label: "No Telp",
      name: "noTelp",
    },
    {
      label: "Email",
      name: "email",
    },
    {
      label: "Address",
      name: "address",
    },
  ],
});

const customNonAdminSpec = generateCustomSpec({
  path: "",
  canBulk: false,
  fields: [
    {
      label: "Company Code",
      name: "companyCode",
    },
    {
      label: "Company Name",
      name: "companyName",
    },
    {
      label: "Department",
      name: "department",
    },
    {
      label: "No Telp",
      name: "noTelp",
    },
    {
      label: "Email",
      name: "email",
    },
    {
      label: "Address",
      name: "address",
    },
  ],
});

const HeaderElement = ({ dataConfig }: { dataConfig: DataConfig }) => {
  const { mutate: exportList, isPending: isDownloading } =
    useExportOrganization();

  const handleExport = () => {
    const params = {
      ...dataConfig,
      limit: undefined,
      page: undefined,
    };

    exportList(
      {
        config: params,
      },
      {
        onError: () => {
          swalErrorDefault.fire();
        },
      },
    );
  };
  return (
    <div className="flex items-center justify-end gap-2">
      <DownloadButton onClick={handleExport} loading={isDownloading} />
    </div>
  );
};

export function OrganizationTable() {
  const [selectedRow, setSelectedRow] = useState<number[]>([]);
  const { dataConfig, setDataConfig } = useDefaultDataConfig();
  const tableForm = useForm();

  const { data: organizations } = useOrganizations(dataConfig);
  const { mutate, isPending } = useOrganizationBulkDelete();

  const { roles } = useCurrentUser();
  const isValidator = roles.includes(ROLE.VALIDATOR_DOCUMENT_OFFICER);
  const isSysAdminView = roles.includes(ROLE.SYS_ADMIN_VIEW);

  function handleBulkDelete() {
    swalDelete.fire({
      preConfirm: () => {
        mutate(
          { ids: selectedRow as unknown[] as string[] },
          {
            onSuccess: () => {
              swalSuccess.fire({
                text: "Organization has been deleted",
              });
            },
            onError: (error) => {
              swalError.fire({
                text: error.message,
              });
            },
            onSettled: () => {
              setSelectedRow([]);
            },
          },
        );
      },
    });
  }

  return (
    <>
      <TableForm
        setDataConfig={setDataConfig}
        form={tableForm}
        createPath={
          isValidator || isSysAdminView
            ? null
            : "/master-data/organization/create"
        }
        selectedIds={selectedRow as unknown as string[]}
        handleBulkDelete={
          isValidator || isSysAdminView ? null : handleBulkDelete
        }
        isPendingDelete={isPending}
      />
      <TableDefault
        spec={isValidator ? customNonAdminSpec : customAdminSpec}
        data={organizations}
        selectedRow={selectedRow}
        setSelectedRow={setSelectedRow}
        dataConfig={dataConfig}
        setDataConfig={setDataConfig}
        searchFilterByColumns={true}
        headerElement={<HeaderElement dataConfig={dataConfig} />}
        customButtonEdit={isSysAdminView ? nullElement : undefined}
      />
    </>
  );
}
